import API from '../services/API'
import { extensionOf } from '../components/Documents/useDocumentPreview'

export const DEMO_UPLOAD_MESSAGE =
  'Document uploads are not available in the demo.'

// Uploads a browser File to S3 through a presigned URL and resolves with the
// new key. Throws on any failure: the API client never rejects on 4xx/5xx, so
// both steps are status-checked before the key is trusted. `onProgress`
// receives the S3 PUT's progress as a whole percentage.
export const uploadDocument = async (file, folder, { onProgress } = {}) => {
  // Prefer the file name's extension - a .docx MIME subtype is
  // "vnd.openxmlformats-…", which makes a key nothing can preview
  const extension = extensionOf(file.name) || file.type.split('/')[1]
  // Handled here rather than by the global handler: on a demo tenant the
  // presign is refused with 403 and the global handler would swallow the
  // response in a never-resolving promise, leaving the form stuck on "Saving"
  const presign = await API.post(
    '/gets3uploadurl',
    { folder, extension },
    { skipGlobalErrorHandler: true },
  )
  if (presign.status !== 200 || !presign.data?.url) {
    throw new Error(
      presign.data?.errorCode === 'demoUploadsDisabled'
        ? DEMO_UPLOAD_MESSAGE
        : 'Unable to prepare the upload.',
    )
  }
  // Large scans can outlast the shared 60s axios timeout, and an S3 error
  // must surface here rather than in the global handler.
  const put = await API.put(presign.data.url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    timeout: 0,
    skipGlobalErrorHandler: true,
    onUploadProgress: (event) => {
      if (event.total && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    },
  })
  if (put.status < 200 || put.status >= 300) {
    throw new Error(`Upload failed (${put.status})`)
  }
  return presign.data.key
}

// The shape records keep when they store the original file's details
// alongside the key (StaffPayInvoice.file, Expense.receipt).
export const fileRecord = (file, key) => ({
  key,
  name: file.name,
  contentType: file.type,
  size: file.size,
})
